/**
 * @file Components/Admin/Dashboard.jsx
 * @description Admin dashboard with real-time stats from API:
 * KPI cards with trends, booking status breakdown, recent bookings,
 * recent users, and popular tours.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, Package, Calendar, MessageSquare, TrendingUp, TrendingDown,
  IndianRupee, Loader2, RefreshCw, Clock, CheckCircle, XCircle,
  Star, ArrowRight, UserPlus
} from 'lucide-react';
import { getAdminStats } from '../../services/api';

const StatCard = ({ title, value, icon, trend, color }) => (
  <div className="bg-white rounded-xl shadow-sm border p-5">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">{title}</p>
        <h3 className="text-2xl font-bold mt-1 text-gray-900">{value}</h3>
        {trend !== undefined && (
          <p className={`flex items-center text-xs mt-2 font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp size={13} className="mr-1" /> : <TrendingDown size={13} className="mr-1" />}
            {trend >= 0 ? '+' : ''}{trend}%
            <span className="text-gray-400 font-normal ml-1">vs last month</span>
          </p>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color}`}>{icon}</div>
    </div>
  </div>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getAdminStats();
      setData(res?.data?.data || res?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin h-8 w-8 text-indigo-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-center py-16 space-y-3">
        <p className="text-red-500">{error}</p>
        <button onClick={fetchStats} className="text-sm text-indigo-600 hover:underline flex items-center gap-1 mx-auto">
          <RefreshCw size={14} /> Try again
        </button>
      </div>
    );
  }

  const c = data?.counts || {};
  const rev = data?.revenue || {};
  const trends = data?.trends || {};
  const bs = data?.bookingStatus || {};

  const statusColor = (s) => {
    if (s === 'confirmed') return 'bg-emerald-100 text-emerald-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="space-y-6">
      {/* Refresh indicator */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Auto-refreshes every 60s</p>
        <button onClick={fetchStats} disabled={loading}
          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Users" value={c.users?.toLocaleString() || '0'}
          icon={<Users size={22} className="text-indigo-600" />} trend={trends.users} color="bg-indigo-50" />
        <StatCard title="Active Tours" value={c.tours?.toLocaleString() || '0'}
          icon={<Package size={22} className="text-blue-600" />} color="bg-blue-50" />
        <StatCard title="Total Bookings" value={c.bookings?.toLocaleString() || '0'}
          icon={<Calendar size={22} className="text-violet-600" />} trend={trends.bookings} color="bg-violet-50" />
        <StatCard title="Total Revenue" value={`₹${(rev.total || 0).toLocaleString()}`}
          icon={<IndianRupee size={22} className="text-emerald-600" />} trend={trends.revenue} color="bg-emerald-50" />
      </div>

      {/* Booking Status + Testimonials */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Booking Status</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-amber-600"><Clock size={14} /> Pending</span>
              <span className="text-lg font-bold text-gray-900">{bs.pending || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-emerald-600"><CheckCircle size={14} /> Confirmed</span>
              <span className="text-lg font-bold text-gray-900">{bs.confirmed || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-red-500"><XCircle size={14} /> Cancelled</span>
              <span className="text-lg font-bold text-gray-900">{bs.cancelled || 0}</span>
            </div>
          </div>
          {(bs.pending || 0) > 0 && (
            <Link to="/admin/bookings" className="mt-4 flex items-center gap-1 text-xs text-indigo-600 hover:underline">
              Review pending bookings <ArrowRight size={12} />
            </Link>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Revenue This Month</h3>
          <p className="text-3xl font-bold text-gray-900">₹{(rev.thisMonth || 0).toLocaleString()}</p>
          <p className="text-xs text-gray-400 mt-1">Last month: ₹{(rev.lastMonth || 0).toLocaleString()}</p>
          {rev.thisMonth > 0 && rev.lastMonth > 0 && (
            <div className="mt-3 w-full bg-gray-100 rounded-full h-2">
              <div className="bg-emerald-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(100, Math.round((rev.thisMonth / Math.max(rev.lastMonth, 1)) * 100))}%` }} />
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Testimonials</span>
              <span className="font-semibold">{c.testimonials || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Avg. Revenue/Booking</span>
              <span className="font-semibold">₹{c.bookings ? Math.round(rev.total / c.bookings).toLocaleString() : 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Conversion Rate</span>
              <span className="font-semibold">{c.bookings && c.users ? Math.round((c.bookings / c.users) * 100) : 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Bookings */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Recent Bookings</h3>
            <Link to="/admin/bookings" className="text-xs text-indigo-600 hover:underline">View all</Link>
          </div>
          {data?.recentBookings?.length > 0 ? (
            <div className="space-y-3">
              {data.recentBookings.map((b, i) => (
                <div key={b._id || i} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{b.customerName}</p>
                    <p className="text-xs text-gray-400">{b.tour?.title || 'Tour'} · {new Date(b.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <span className="text-sm font-semibold text-gray-700">₹{(b.totalPrice || 0).toLocaleString()}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${statusColor(b.status)}`}>
                      {b.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">No bookings yet</p>
          )}
        </div>

        {/* Recent Users */}
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Recent Users</h3>
            <Link to="/admin/users" className="text-xs text-indigo-600 hover:underline">View all</Link>
          </div>
          {data?.recentUsers?.length > 0 ? (
            <div className="space-y-3">
              {data.recentUsers.map((u, i) => (
                <div key={u._id || i} className="flex items-center gap-3 py-2 border-b last:border-0">
                  <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-600 font-semibold text-sm">{u.name?.charAt(0)?.toUpperCase() || '?'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                    }`}>{u.role}</span>
                    <p className="text-[10px] text-gray-400 mt-0.5">{new Date(u.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-6">No users yet</p>
          )}
        </div>
      </div>

      {/* Popular Tours */}
      {data?.popularTours?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700">Popular Tours</h3>
            <Link to="/admin/tours" className="text-xs text-indigo-600 hover:underline">Manage tours</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.popularTours.map((t, i) => (
              <div key={t._id || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {(t.thumbnail || t.images?.[0]) ? (
                  <img src={t.thumbnail || t.images[0]} alt={t.title} className="h-10 w-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <Package size={16} className="text-indigo-500" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-900 truncate">{t.title}</p>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    <span>₹{(t.price || 0).toLocaleString()}</span>
                    {t.rating && <span className="flex items-center gap-0.5"><Star size={9} className="text-amber-400 fill-current" />{t.rating.toFixed(1)}</span>}
                    <span>{t.bookingsCount || 0} bookings</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;